import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Save } from "lucide-react";
import { useDesignSystem, cn } from '../../designSystem';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

export function TemplateDialogForm({ 
  isOpen, 
  onClose, 
  initialTemplate, 
  onSubmit, 
  isEditing = false, 
  isSaving = false 
}) {
  const { colors, typography, effects, componentStyles } = useDesignSystem();
  
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });
  
  // Reset form data when dialog opens with new template data
  useEffect(() => {
    if (isOpen) {
      if (initialTemplate) {
        setFormData({
          title: initialTemplate.title || '',
          content: initialTemplate.content || ''
        });
      } else {
        setFormData({
          title: '',
          content: ''
        });
      }
    }
  }, [isOpen, initialTemplate]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) return;
    onSubmit(formData);
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={onClose}
    >
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col dark:bg-neutral-800">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {isEditing ? 'Rediger mal' : 'Legg til mal'}
          </DialogTitle>
          <DialogDescription className="text-sm dark:text-neutral-400">
            {isEditing ? 'Oppdater informasjonen for malen.' : 'Fyll ut informasjonen under for å legge til en ny mal.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-grow overflow-y-auto py-4 px-1">
          <ScrollArea className="h-full pr-4">
            <form id="template-form" onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <label htmlFor="title" className="text-sm font-medium dark:text-neutral-300">
                    Tittel
                  </label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Malens tittel"
                    required
                    className="w-full dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:ring-offset-0"
                  />
                </div>
                
                {/* Content */}
                <div className="space-y-2">
                  <label htmlFor="content" className="text-sm font-medium dark:text-neutral-300">
                    Innhold
                  </label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Skriv inn malens innhold her..."
                    required
                    className="min-h-[200px] resize-none w-full dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:ring-offset-0"
                  />
                </div>
              </div>
            </form>
          </ScrollArea>
        </div>
        
        <DialogFooter className="border-t dark:border-neutral-700 pt-4 sm:flex-row flex-col gap-2 sm:gap-0 p-4">
          <div className="w-full sm:w-auto order-2 sm:order-1">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onClose(false)}
              className="w-full sm:w-auto dark:bg-neutral-700 dark:text-neutral-100 dark:border-neutral-600 dark:hover:bg-neutral-600"
            >
              Avbryt
            </Button>
          </div>
          <div className="w-full sm:w-auto order-1 sm:order-2">
            <Button 
              type="submit"
              form="template-form"
              disabled={isSaving || !formData.title || !formData.content}
              className={cn(
                "w-full sm:w-auto",
                "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700",
                "text-white shadow transition-all duration-200",
                isSaving && "opacity-70 cursor-not-allowed"
              )}
            >
              {isSaving ? (
                <>
                  <div className="mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  Lagrer...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditing ? 'Oppdater' : 'Lagre'}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateDialogForm;